import { memo, useEffect, useRef, useState } from "react";
import SlideBackdrop from "../../../canvas/design/SlideBackdrop.jsx";
import {
  BACKGROUND_CATEGORIES,
  BACKGROUND_PRESETS,
} from "../../../canvas/design/backgrounds";
import { DEFAULT_BACKGROUND_ADJUST } from "../../../canvas/design/slideDesign";
import SlideMiniPreview from "../SlideMiniPreview";
import { RangeField, ScopePicker, scopeLabel, useStageScale } from "./studioShared";

const Swatch = memo(function Swatch({ background, label, active, onSelect }) {
  const [ref, scale] = useStageScale();
  return (
    <button
      type="button"
      ref={ref}
      className={`cine-bg-swatch${active ? " is-active" : ""}`}
      onClick={onSelect}
      aria-pressed={active}
      aria-label={label}
      title={label}
    >
      <span
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: 1280,
          height: 720,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
        }}
      >
        <SlideBackdrop background={background} still />
      </span>
      <span className="cine-bg-swatch-label">{label}</span>
    </button>
  );
});

const sameBackground = (a, b) => {
  if (!a || !b || a.kind !== b.kind) return false;
  if (a.kind === "preset") return a.presetId === b.presetId;
  if (a.kind === "solid") return a.color === b.color;
  if (a.kind === "image") return a.src === b.src;
  return false;
};

const POSITIONS = ["center", "top", "bottom", "left", "right"];

export default function BackgroundTab({
  currentBackground,
  currentElements,
  slideCount,
  currentIndex,
  onApplyBackground,
  onUploadImage,
  uploadedImages,
  classicBackgrounds = [],
  classicColors = [],
}) {
  const categories = classicBackgrounds.length
    ? [{ id: "classic", label: "Classic" }, ...BACKGROUND_CATEGORIES]
    : BACKGROUND_CATEGORIES;
  const [category, setCategory] = useState(categories[0].id);
  const [draft, setDraft] = useState(() => currentBackground || null);
  const [scope, setScope] = useState("all");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // Follow the slide when the professor switches slides.
  useEffect(() => {
    setDraft(currentBackground || null);
  }, [currentIndex]);

  const adjust = { ...DEFAULT_BACKGROUND_ADJUST, ...(draft?.adjust || {}) };
  const setAdjust = (patch) =>
    setDraft((d) => (d ? { ...d, adjust: { ...DEFAULT_BACKGROUND_ADJUST, ...(d.adjust || {}), ...patch } } : d));

  const pick = (background) =>
    setDraft((d) => ({ ...background, adjust: d?.adjust ? { ...d.adjust } : { ...DEFAULT_BACKGROUND_ADJUST } }));

  const apply = async (target) => {
    if (!draft) return;
    setBusy(true);
    try {
      await onApplyBackground(draft, target);
    } finally {
      setBusy(false);
    }
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await onUploadImage(file);
      if (url) pick({ kind: "image", src: url, fit: "cover", position: "center", scale: 100 });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const gradientStops = draft?.kind === "gradient" ? draft.stops || ["#0f172a", "#1e3a8a"] : ["#0f172a", "#1e3a8a"];

  return (
    <>
      <div className="cine-live-preview">
        <span className="cine-live-preview-badge">{draft ? "Live preview" : "Current slide"}</span>
        <SlideMiniPreview elements={currentElements} background={draft} animated />
      </div>

      <div className="cine-chips" role="tablist" aria-label="Background categories">
        {categories.map((c) => (
          <button
            key={c.id}
            type="button"
            role="tab"
            aria-selected={category === c.id}
            className={`cine-chip${category === c.id ? " is-active" : ""}`}
            onClick={() => setCategory(c.id)}
          >
            {c.label}
          </button>
        ))}
      </div>

      {category === "classic" ? (
        <>
          {classicColors.length ? (
            <>
              <span className="cine-studio-label">Solid colour</span>
              <div className="cine-row" style={{ flexWrap: "wrap" }}>
                <input
                  type="color"
                  className="cine-color"
                  value={draft?.kind === "solid" ? draft.color : "#f8fafc"}
                  onChange={(e) => pick({ kind: "solid", color: e.target.value })}
                  aria-label="Solid background colour"
                />
                {classicColors.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`cine-bg-swatch${sameBackground(draft, { kind: "solid", color }) ? " is-active" : ""}`}
                    style={{ width: 26, height: 26, aspectRatio: "auto", background: color, borderRadius: 8 }}
                    onClick={() => pick({ kind: "solid", color })}
                    aria-label={`Colour ${color}`}
                  />
                ))}
              </div>
            </>
          ) : null}
          <span className="cine-studio-label">Backgrounds</span>
          <div className="cine-bg-grid">
            {classicBackgrounds.map((bg) => {
              const value = { kind: "image", src: bg.src, fit: "cover", position: "center", scale: 100 };
              return (
                <button
                  key={bg.key}
                  type="button"
                  className={`cine-bg-swatch${sameBackground(draft, value) ? " is-active" : ""}`}
                  style={{ backgroundImage: `url("${bg.src}")`, backgroundSize: "cover", backgroundPosition: "center" }}
                  onClick={() => pick(value)}
                  aria-pressed={sameBackground(draft, value)}
                  aria-label={bg.label}
                  title={bg.label}
                >
                  <span className="cine-bg-swatch-label">{bg.label}</span>
                </button>
              );
            })}
          </div>
        </>
      ) : category !== "custom" ? (
        <>
          {category === "scientific" ? (
            <p className="cine-studio-sub" style={{ marginTop: 10 }}>
              Kept deliberately faint so histology and anatomy stay the focus.
            </p>
          ) : null}
          <div className="cine-bg-grid" style={{ marginTop: 10 }}>
            {BACKGROUND_PRESETS.filter((p) => p.category === category).map((preset) => {
              const bg = { kind: "preset", presetId: preset.id };
              return (
                <Swatch
                  key={preset.id}
                  background={bg}
                  label={preset.label}
                  active={sameBackground(draft, bg)}
                  onSelect={() => pick(bg)}
                />
              );
            })}
          </div>
        </>
      ) : (
        <div style={{ marginTop: 10 }}>
          <span className="cine-studio-label">Solid colour</span>
          <div className="cine-row">
            <input
              type="color"
              className="cine-color"
              value={draft?.kind === "solid" ? draft.color : "#0f172a"}
              onChange={(e) => pick({ kind: "solid", color: e.target.value })}
              aria-label="Solid background colour"
            />
            <div className="cine-chips">
              {["#ffffff", "#f8fafc", "#0f172a", "#111827", "#0b2545", "#052e2b"].map((color) => (
                <button
                  key={color}
                  type="button"
                  className="cine-bg-swatch"
                  style={{ width: 26, height: 26, aspectRatio: "auto", background: color, borderRadius: 8 }}
                  onClick={() => pick({ kind: "solid", color })}
                  aria-label={`Colour ${color}`}
                />
              ))}
            </div>
          </div>

          <span className="cine-studio-label">Custom gradient</span>
          <div className="cine-row">
            {[0, 1, 2].map((i) => (
              <input
                key={i}
                type="color"
                className="cine-color"
                value={gradientStops[i] || "#38bdf8"}
                aria-label={`Gradient colour ${i + 1}`}
                onChange={(e) => {
                  const stops = [...gradientStops];
                  stops[i] = e.target.value;
                  pick({ kind: "gradient", angle: draft?.kind === "gradient" ? draft.angle ?? 135 : 135, stops });
                }}
              />
            ))}
            {gradientStops.length > 2 && draft?.kind === "gradient" ? (
              <button
                type="button"
                className="cine-btn-secondary"
                onClick={() => setDraft((d) => ({ ...d, stops: d.stops.slice(0, 2) }))}
              >
                2 colours
              </button>
            ) : null}
          </div>
          {draft?.kind === "gradient" ? (
            <RangeField
              label="Angle"
              value={draft.angle ?? 135}
              min={0}
              max={360}
              unit="°"
              onChange={(angle) => setDraft((d) => ({ ...d, angle }))}
            />
          ) : null}

          <span className="cine-studio-label">Image</span>
          <div className="cine-row">
            <button
              type="button"
              className="cine-btn-secondary"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? "Uploading…" : "Upload image"}
            </button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleFile} />
            <input
              className="cine-field cine-grow"
              placeholder="or paste image URL"
              defaultValue={draft?.kind === "image" ? draft.src : ""}
              onBlur={(e) => {
                const src = e.target.value.trim();
                if (src) pick({ kind: "image", src, fit: "cover", position: "center", scale: 100 });
              }}
            />
          </div>
          {uploadedImages?.length ? (
            <div className="cine-bg-grid" style={{ marginTop: 8, gridTemplateColumns: "repeat(4, 1fr)" }}>
              {uploadedImages.slice(0, 12).map((img) => (
                <button
                  key={img.url}
                  type="button"
                  className={`cine-bg-swatch${draft?.kind === "image" && draft.src === img.url ? " is-active" : ""}`}
                  style={{ backgroundImage: `url("${img.url}")`, backgroundSize: "cover", backgroundPosition: "center" }}
                  onClick={() => pick({ kind: "image", src: img.url, fit: "cover", position: "center", scale: 100 })}
                  aria-label={img.name || "Uploaded image"}
                  title={img.name || ""}
                />
              ))}
            </div>
          ) : null}
          {draft?.kind === "image" ? (
            <>
              <div className="cine-row" style={{ marginTop: 10 }}>
                <select
                  className="cine-field"
                  value={draft.fit || "cover"}
                  onChange={(e) => setDraft((d) => ({ ...d, fit: e.target.value }))}
                  aria-label="Image scaling"
                >
                  <option value="cover">Fill (crop)</option>
                  <option value="contain">Fit</option>
                  <option value="fill">Stretch</option>
                  <option value="tile">Tile</option>
                </select>
                <select
                  className="cine-field"
                  value={draft.position || "center"}
                  onChange={(e) => setDraft((d) => ({ ...d, position: e.target.value }))}
                  aria-label="Image position"
                >
                  {POSITIONS.map((p) => (
                    <option key={p} value={p}>
                      {p[0].toUpperCase() + p.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              <RangeField
                label="Scale"
                value={draft.scale ?? 100}
                min={50}
                max={250}
                unit="%"
                onChange={(value) => setDraft((d) => ({ ...d, scale: value }))}
              />
            </>
          ) : null}
        </div>
      )}

      {draft ? (
        <>
          <span className="cine-studio-label">Adjust</span>
          <RangeField label="Brightness" value={adjust.brightness} min={40} max={160} unit="%" onChange={(v) => setAdjust({ brightness: v })} />
          <RangeField label="Blur" value={adjust.blur} min={0} max={24} unit="px" onChange={(v) => setAdjust({ blur: v })} />
          <RangeField label="Opacity" value={adjust.opacity} min={10} max={100} unit="%" onChange={(v) => setAdjust({ opacity: v })} />
          <div className="cine-row" style={{ margin: "8px 0" }}>
            <span style={{ fontSize: 12, width: 82 }}>Overlay</span>
            <div className="cine-chips">
              {["none", "dark", "light"].map((o) => (
                <button
                  key={o}
                  type="button"
                  className={`cine-chip${adjust.overlay === o ? " is-active" : ""}`}
                  onClick={() => setAdjust({ overlay: o })}
                >
                  {o === "none" ? "None" : o === "dark" ? "Dark" : "Light"}
                </button>
              ))}
            </div>
          </div>
          {adjust.overlay !== "none" ? (
            <RangeField label="Strength" value={adjust.overlayStrength} min={5} max={85} unit="%" onChange={(v) => setAdjust({ overlayStrength: v })} />
          ) : null}

          <span className="cine-studio-label">Apply to</span>
          <ScopePicker value={scope} onChange={setScope} slideCount={slideCount} currentIndex={currentIndex} />
          <div className="cine-row" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="cine-btn-primary cine-grow"
              disabled={busy || (Array.isArray(scope) && !scope.length)}
              onClick={() => apply(scope)}
            >
              {busy ? "Applying…" : `Apply to ${scopeLabel(scope, slideCount)}`}
            </button>
            <button
              type="button"
              className="cine-btn-secondary"
              disabled={busy}
              onClick={() => setDraft(currentBackground || null)}
              title="Discard preview"
            >
              Reset
            </button>
          </div>
          {currentBackground ? (
            <button
              type="button"
              className="cine-btn-secondary"
              style={{ marginTop: 8, width: "100%" }}
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await onApplyBackground(null, "current");
                  setDraft(null);
                } finally {
                  setBusy(false);
                }
              }}
            >
              Remove studio background from this slide
            </button>
          ) : null}
        </>
      ) : null}
    </>
  );
}
